import Vue from "vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import * as MapVesselUtil from "@/utils/MapVesselUtil";
import * as turfHelpers from "@turf/helpers";
import { FAVORITE_KEY } from "@/config/const";

const vesselApi = AiseaApiFactory.get("vessel");
const plotApi = AiseaApiFactory.get("plot");
const constantApi = AiseaApiFactory.get("constant");
const enterpriseVesselApi = AiseaApiFactory.get("enterpriseVessel");
const voyageHistoryApi = AiseaApiFactory.get("voyageHistory");
const polarisApi = AiseaApiFactory.get("polaris");
const cameraApi = AiseaApiFactory.get("enterpriseCamera");
const recordApi = AiseaApiFactory.get("enterpriseVoyageRecords");

import { db } from "@/firebase/firebaseConfig.js";
import { WakeLineUtil } from "@/utils";
import LocalDbService from "@/services/localDbService";
import FireStoreService from "@/services/fireStoreService";
import { Map } from "immutable";
import { GeoUtil } from "@/utils";
import moment from "moment-timezone";
import _ from "lodash";
import { CAMERA_MOVIE_TYPE } from "@/config/const";
import firebase from "@firebase/app";

// Service
import AuthService from "@/services/authService";

export default {
  /*=========================================================================================
    Map系 Actions
  ==========================================================================================*/
  async fetchCountryCode({ commit }) {
    let res = await constantApi.fetchCountryCode();
    commit("SET_COUNTRY_CODE", res.data.country_codes);
    return res.data;
  },

  async searchVessels({ commit }, query) {
    let res = await vesselApi.search(query);
    let resultVessels = [];
    res.data.vessels.forEach(vessel => {
      let obj = {
        id: vessel.id,
        name: vessel.name,
        name_ja: vessel.name_ja,
        categoryId: vessel.vessel_category.id,
        imo: vessel.imo,
        mmsi: vessel.mmsi,
      };
      resultVessels.push(obj);
    });
    commit("SET_VESSEL_SEARCH_RESULT", resultVessels);
    return true;
  },

  async showVesselDetail({ commit, state, getters }, vesselId) {
    commit("SET_VESSEL_DETAIL", null);
    commit("SET_VESSEL_PICTURES", null);
    if (state.unsubscribes.length !== 0) {
      state.unsubscribes.forEach(unsubscribe => {
        unsubscribe();
      });
      commit("SET_UNSUBSCRIBES", []);
    }

    commit("SET_SHOW_VESSEL_DETAIL", true);
    commit("SET_SHOW_PLOT_DETAIL", false);

    let res = await vesselApi.getVessel(vesselId);
    commit("SET_VESSEL_DETAIL", res.data);

    let pictures = [];
    if (res.data.pictures.length === 0) {
      pictures.push({
        id: "0",
        thumbnail_path: require("@/assets/images/no_image_bg.svg"),
      });
    } else {
      pictures = res.data.pictures;
    }
    commit("SET_VESSEL_PICTURES", pictures);

    if (getters.isOwnVessel(vesselId)) {
      let cameras = [];
      let radars = [];
      let res = await enterpriseVesselApi.fetchCameras(vesselId);
      if (res.data.cameras.length === 0) {
        let data = {
          id: "0",
        };
        cameras.push(data);
        radars.push(data);
      } else {
        res.data.cameras.forEach(camera => {
          if (
            camera.movie_type === CAMERA_MOVIE_TYPE.CAMERA ||
            camera.movie_type === CAMERA_MOVIE_TYPE.HLS_CAMERA ||
            camera.movie_type === CAMERA_MOVIE_TYPE.MEDIAMTX
          ) {
            cameras.push(camera);
          } else if (
            camera.movie_type === CAMERA_MOVIE_TYPE.RADAR ||
            camera.movie_type === CAMERA_MOVIE_TYPE.HLS_RADAR
          ) {
            radars.push(camera);
          }
        });
        if (cameras.length === 0) {
          cameras.push({
            id: "0",
          });
        }
        if (radars.length === 0) {
          cameras.push({
            id: "0",
          });
        }
      }
      commit("SET_VESSEL_CAMERAS", cameras);
      commit("SET_VESSEL_RADARS", radars);
    } else {
      commit("SET_MONITORING_STATUS", getters.isMonitoring(vesselId));
    }

    let unsubscribes = [];
    let unsubscribeVesselInfo = await db
      .collection("vessel_info")
      .doc(vesselId)
      .onSnapshot(doc => {
        commit("SET_VESSEL_DETAIL_INFO", doc.data());
      });
    unsubscribes.push(unsubscribeVesselInfo);

    const collectionName = await AuthService.getVesselPositionCollectionName();
    let unsubscribeVesselPosition = await db
      .collection(collectionName)
      .doc(vesselId)
      .onSnapshot(doc => {
        commit("SET_VESSEL_DETAIL_POSITION", doc.data());
      });
    unsubscribes.push(unsubscribeVesselPosition);
    commit("SET_UNSUBSCRIBES", unsubscribes);
  },

  async cleanUpVesselDetail({ commit, state }) {
    commit("SET_SHOW_VESSEL_DETAIL", false);
    if (state.unsubscribes.length !== 0) {
      state.unsubscribes.forEach(unsubscribe => unsubscribe());
      commit("SET_UNSUBSCRIBES", []);
    }
  },

  async jumpTo({ commit }, coordinates) {
    commit("SET_JUMP_TO", coordinates);
  },

  async jumpToMap({ commit }, coordinates) {
    commit("SET_JUMP_TO_MAP", coordinates);
  },

  // eslint-disable-next-line no-unused-vars
  async getLastPosition(_, coordinates) {
    let res = await voyageHistoryApi.getLastPosition(coordinates);
    return res.data;
  },

  /*=========================================================================================
   選択船 Actions
  ==========================================================================================*/
  async fetchMonitoringVessels({ commit }) {
    commit("SET_SELECTED_VESSELS", []);
    let vesselIds = await LocalDbService.getMonitoringVessels();
    vesselIds.forEach(async vesselId => {
      let res = await vesselApi.getVessel(vesselId);
      if (res.status) {
        commit("ADD_MONITORING_VESSELS", res.data);
      }
    });
  },

  async setSelectedVesselPosition({ commit }, payload) {
    commit("SET_SELECTED_VESSEL_POSITION", payload);
  },

  async addMonitoringVessel({ commit, getters }, vesselId) {
    let vesselInfo = await vesselApi.getVessel(vesselId);
    if (vesselInfo.data) {
      if (!getters.isMonitoring(vesselId)) {
        let vesselIds = await LocalDbService.addMonitoringVessel(vesselId);
        await FireStoreService.saveSelectedVessels(vesselIds);
        commit("ADD_MONITORING_VESSELS", vesselInfo.data);
        commit("SET_IS_NEW_SELECTED_VESSEL", true);
      }
      return vesselInfo.data;
    } else {
      throw new Error({
        message: "Error adding monitoring vessel",
        vesselId: vesselId,
      });
    }
  },

  async setIsNewSelectedVessel({ commit }) {
    commit("SET_IS_NEW_SELECTED_VESSEL", false);
  },

  async removeMonitoringVessel({ commit, state }, vesselId) {
    let vesselIds = await LocalDbService.removeMonitoringVessel(vesselId);
    await FireStoreService.saveSelectedVessels(vesselIds);
    let removeVessel;
    let result = state.monitoringVessels.filter(function(value) {
      if (value.id === vesselId) {
        removeVessel = value;
      }
      return value.id !== vesselId;
    });
    commit("SET_SELECTED_VESSELS", result);

    if (state.vesselDetail) {
      if (state.vesselDetail.id === vesselId) {
        let test_copy = { ...state.vesselDetail };
        commit("SET_VESSEL_DETAIL", test_copy);
      }
    }

    commit("REMOVE_WAKE", vesselId);
    commit("REMOVE_SELECTED_VESSEL_POSITION", vesselId);

    return removeVessel;
  },

  /*=========================================================================================
   自社船 Actions
  ==========================================================================================*/
  async fetchOwnVessels({ commit }) {
    let payload = {
      is_disabled: false,
      is_deleted: false,
      page: 0,
      limit: 200,
    };
    let res = await enterpriseVesselApi.search(payload);
    commit("SET_OWN_VESSELS", res.data.vessels);
    return res.data;
  },

  async setOwnVesselPosition({ commit }, payload) {
    commit("SET_OWN_VESSEL_POSITION", payload);
  },

  async setOwnVesselDisplayDurationTime({ commit }, time) {
    commit("SET_OWN_VESSEL_DISPLAY_DURATION_TIME", time);
  },

  /*=========================================================================================
    プロット系 Actions
  ==========================================================================================*/
  async cleanUpPlotDetail({ commit }) {
    commit("SET_SHOW_PLOT_DETAIL", false);
  },

  async showPlotDetail({ commit }, plotId) {
    commit("SET_PLOT_DETAIL", null);
    commit("SET_SHOW_PLOT_DETAIL", true);
    commit("SET_SHOW_VESSEL_DETAIL", false);

    let res = await plotApi.get(plotId);

    if (res.data.enterprise_id) {
      let data = await AiseaApiFactory.get("enterprisePublic").get(
        res.data.enterprise_id
      );
      res.data.enterprise_public = data;
    }
    if (res.data.user_id) {
      let data = await AiseaApiFactory.get("userPublic").get(res.data.user_id);
      res.data.user_public = data;
    }
    commit("SET_PLOT_DETAIL", res.data);
  },

  /*=========================================================================================
    航跡系 Actions
  ==========================================================================================*/
  async fetchWake({ commit, state }, payload) {
    let res = await recordApi.fetchWakeByVesselId(payload);
    let positions = res.data.p;
    // coordinateは最低2以上必要なので無い場合は作成しない
    if (positions.length <= 1) {
      throw new Error({
        message: "positions are less then two",
        target: payload,
      });
    }

    // 重複データ削除
    let filterPositions = positions.filter(function(v1, i1, a1) {
      return (
        a1.findIndex(v2 => {
          return v1.d === v2.d;
        }) === i1
      );
    });

    // ソート
    filterPositions.sort((a, b) => {
      return a.d > b.d ? 1 : -1;
    });

    // 船舶情報
    let vesselInfo = await vesselApi.getVessel(payload.vesselId);
    let vesselName =
      vesselInfo.data.name_ja !== null
        ? vesselInfo.data.name_ja
        : vesselInfo.data.name;

    // 速さによる色つけ
    let positionWithColor = [];
    filterPositions.forEach(p => {
      p["vesselName"] = vesselName;
      p["localDate"] = moment
        .utc(p.d)
        .local()
        .format("YYYY/MM/DD HH:mm");
      p["color"] = WakeLineUtil.getVelocityColor(p.v);
      p["opacity"] = 0.9;
      positionWithColor.push(p);
    });

    // TODO ロジックAIS情報が飛んでいる時は出さないロジック(検討後に導入)
    // positionWithColor.forEach(p => {
    //   console.log(`${p.d} == ${p.v} == ${p.color} == ${p.lat},${p.lon}`);
    // });
    // let positionWithColor = [];
    // let tmp = null;
    // filterPositions.forEach(p => {
    //   p["color"] = WakeLineUtil.getVelocityColor(p.v);
    //   p["opacity"] = 0.9;
    //   if (tmp) {
    //     if (DateUtil.isOverTime(p.d, tmp.d, "hours")) {
    //       p["color"] = "#FFFFFF";
    //       p["opacity"] = 0.2;
    //       positionWithColor.slice(-1)[0].color = "#FFFFFF";
    //       positionWithColor.slice(-1)[0].opacity = 0.2;
    //     }
    //   }
    //   positionWithColor.push(p);
    //   tmp = p;
    // });

    // 表示上限５件を超えたら、古いの消す
    if (state.wakes.size >= 5) {
      if (state.oldestVesselId) {
        commit("REMOVE_OLDEST_WAKE", state.oldestVesselId);
      }
    }

    // 一番古い船舶IDを保持
    if (state.wakes.size === 0) {
      commit("SET_OLDEST_VESSEL_ID", payload.vesselId);
    }

    commit("SET_WAKES", {
      vesselId: payload.vesselId,
      wakes: positionWithColor,
    });
  },

  async mergeWakes({ commit, state }, payload) {
    let wakes = state.wakes;
    let wake = wakes.get(payload.vesselId);
    if (wake) {
      let vesselInfo = await vesselApi.getVessel(payload.vesselId);
      let vesselName =
        vesselInfo.data.name_ja !== null
          ? vesselInfo.data.name_ja
          : vesselInfo.data.name;
      payload.data["vesselName"] = vesselName;
      let color = WakeLineUtil.getVelocityColor(payload.data.v);
      payload.data["color"] = color;
      payload.data["localDate"] = moment
        .utc(payload.t)
        .local()
        .format("YYYY/MM/DD HH:mm");
      commit("MERGE_WAKES", payload);
    }
  },

  async removeWake({ commit }, vesselId) {
    commit("REMOVE_WAKE", vesselId);
  },

  async crateWakesLineFeature({ state, commit }) {
    let promiseJob = [];
    state.wakes.entrySeq().forEach(e => {
      let job = new Promise(resolve => {
        resolve(WakeLineUtil.crateWakeLineFeature(e[1]));
      });
      promiseJob.push(job);
    });

    let features = [];
    Promise.all(promiseJob).then(res => {
      res.forEach(f => {
        features = _.concat(features, f);
      });
      let featureCollection = MapVesselUtil.createFeatureCollection(features);
      features = [];
      commit("SET_WAKES_LINE_FEATURE_COLLECTION", featureCollection);
    });
  },

  /*=========================================================================================
   フィルター系 Actions
  ==========================================================================================*/
  setFilterVessels({ commit }, payload) {
    commit("SET_FILTER_VESSELS", payload);
  },

  setFilterPlots({ commit }, payload) {
    commit("SET_FILTER_PLOTS", payload);
  },

  setFilterNavigations({ commit }, payload) {
    commit("SET_FILTER_NAVIGATIONS", payload);
  },

  /*=========================================================================================
   POLARIS系 Actions
  ==========================================================================================*/
  async fetchPolarisOffingWavePoint({ commit }) {
    let user = await AuthService.getUser();
    let res = await FireStoreService.get("weather", user.account_id);
    if (res === null) return;
    let pointsData = [];
    if (res.polaris_offing_wave_points) {
      pointsData = res.polaris_offing_wave_points;
    }
    commit("SET_OFFING_WAVE_POINT", pointsData);
  },
  async getOffingWavePointInfo(_, payload) {
    let res = await polarisApi.getOffingWavePoint({
      lng: payload.coordinate[0],
      lat: payload.coordinate[1],
      period: 72,
    });

    let requestTime = moment
      .utc()
      .local()
      .format("YYYY/MM/DD HH:mm:ss");
    let initialTime = moment
      .utc(res.data.initialTime)
      .local()
      .format("YYYY/MM/DD HH:mm:ss");
    let pointInfoList = res.data.pointInfoList;
    let windSpeed = [];
    let windDirection = [];
    let waveDirection = [];
    let waveHeight = [];
    let wavePeriod = [];
    let currentDirection = [];
    let currentSpeed = [];
    let time = [];
    pointInfoList.forEach(v => {
      windSpeed.push(v.windSpeed);
      windDirection.push(v.windDirection);
      waveDirection.push(v.waveDirection);
      waveHeight.push(v.waveHeight);
      wavePeriod.push(v.wavePeriod);
      currentDirection.push(v.currentDirection);
      currentSpeed.push(v.currentSpeed);
      time.push(v.time);
    });
    let pointData = {
      windData: {
        windSpeed: windSpeed,
        windDirection: windDirection,
        initialTime: initialTime,
      },
      waveData: {
        waveDirection: waveDirection,
        waveHeight: waveHeight,
        wavePeriod: wavePeriod,
        initialTime: initialTime,
      },
      currentData: {
        currentDirection: currentDirection,
        currentSpeed: currentSpeed,
        initialTime: initialTime,
      },
    };

    let point = {
      pointId: payload.pointId,
      pointName: payload.pointName,
      requestTime: requestTime,
      initialTime: initialTime,
      coordinate: payload.coordinate,
      pointData: pointData,
    };

    return point;
  },

  async getTyphoonInfo({ commit }) {
    let requestTime = moment()
      .local()
      .format("YYYY/MM/DD(dd) HH:mm");
    let res = await polarisApi.getTyphoonInfo();
    if (res.data.status) {
      if (
        res.data.typhoonInfoList.typhoonInfo === null ||
        typeof res.data.typhoonInfoList.typhoonInfo === "undefined"
      )
        return;
      let result = [];
      let typhoonInfoList = res.data.typhoonInfoList.typhoonInfo;
      typhoonInfoList.forEach(typhoonInfo => {
        // 過去の経路作成
        let historyTracks = [];
        typhoonInfo.course.trackCoordinate.forEach(track => {
          let trackSplit = track.split(",");
          let trackDegree = GeoUtil.d_msToDegree([
            trackSplit[0],
            trackSplit[1],
          ]);
          historyTracks.push(trackDegree);
        });
        // 予報台風の経路作成
        let foreacastData = [];
        typhoonInfo.forecast.forEach(forecast => {
          let centerCoordinateSplit = forecast.centerPart.centerCoordinate.split(
            ","
          );
          let centerCoordinateDegree = GeoUtil.d_msToDegree([
            centerCoordinateSplit[0],
            centerCoordinateSplit[1],
          ]);
          foreacastData.push({
            coordinate: centerCoordinateDegree,
            props: forecast,
          });
        });
        // 現在地台風の経路作成
        let nowcastData = [];
        typhoonInfo.nowcast.forEach(nowcast => {
          let centerCoordinateSplit = nowcast.centerPart.centerCoordinate.split(
            ","
          );
          let centerCoordinateDegree = GeoUtil.d_msToDegree([
            centerCoordinateSplit[0],
            centerCoordinateSplit[1],
          ]);
          nowcastData.push({
            coordinate: centerCoordinateDegree,
            props: nowcast,
          });
        });

        result.push({
          requestTime: requestTime,
          initialTime: typhoonInfo.initialTime,
          name: typhoonInfo.name,
          nameKana: typhoonInfo.nameKana,
          number: typhoonInfo.number,
          historyTracks: historyTracks,
          foreacastData: foreacastData,
          nowcastData: nowcastData,
        });
      });

      commit("SET_TYPHOON_INFO_LIST", result);
    } else {
      commit("SET_TYPHOON_INFO_LIST", []);
    }

    return true;
  },

  /*=========================================================================================
   定点カメラ Actions
  ==========================================================================================*/
  async fetchFixedPointCameras() {
    let res = await cameraApi.search();
    if (res.data.cameras.length !== 0) {
      let fixedPointCameraFeatures = [];
      res.data.cameras.forEach(camera => {
        if (
          camera.latitude !== null &&
          camera.longitude !== null &&
          (camera.movie_type === CAMERA_MOVIE_TYPE.HLS_CAMERA || camera.movie_type === CAMERA_MOVIE_TYPE.MEDIAMTX ||
            camera.movie_type === CAMERA_MOVIE_TYPE.CAMERA)
        ) {
          let isTextTop;
          if (camera.direction > 90 && camera.direction < 270) {
            isTextTop = true;
          } else {
            isTextTop = false;
          }
          let feature = turfHelpers.point([camera.longitude, camera.latitude], {
            cameraId: camera.id,
            cameraType: camera.movie_type,
            name: camera.name,
            direction: camera.direction,
            icon: "fixed-point-camera-unselected",
            isTextTop: isTextTop,
          });
          fixedPointCameraFeatures.push(feature);
        }
      });

      return turfHelpers.featureCollection(fixedPointCameraFeatures);
    }

    return [];
  },

  /*=========================================================================================
   お気に入りグループ系 Actions
  ==========================================================================================*/

  async checkFirestoreDataReady() {
    // お気に入りdocumentが3つ存在していたらtrueを返す
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    let favoritesLength = await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .where(firebase.firestore.FieldPath.documentId(), "in", ["0", "1", "2"])
      .get()
      .then(snap => {
        return snap.size;
      });
    return favoritesLength == 3;
  },

  async createFirestoreData() {
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    const favoriteKeys = ["0", "1", "2"];
    const promise = [];
    for (const favoriteKey of favoriteKeys) {
      promise.push(
        new Promise((resolve, reject) => {
          db.collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey)
            .collection("groups")
            .doc("existFlag")
            .set({
              name: "exist",
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              reject(error);
            });
        })
      );

      promise.push(
        new Promise((resolve, reject) => {
          db.collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey)
            .set({
              label: "お気に入り" + (Number(favoriteKey) + 1),
            })
            .then(() => {
              resolve();
            })
            .catch(error => {
              reject(error);
            });
        })
      );
    }

    Promise.all(promise);
  },

  async subscribeGroups({ commit }) {
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    const favoriteKeys = [
      FAVORITE_KEY.FAVORITE_FIRST,
      FAVORITE_KEY.FAVORITE_SECOND,
      FAVORITE_KEY.FAVORITE_THIRD,
    ];
    const promise = [];
    for (const favoriteKey of favoriteKeys) {
      promise.push(
        new Promise(() => {
          const unsubscribe = db
            .collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey)
            .collection("groups")
            .onSnapshot(querySnapshot => {
              const groups = [];
              querySnapshot.forEach(doc => {
                console.log(doc);
                if (doc.id !== "existFlag")
                  groups.push({ documentId: doc.id, ...doc.data() });
              });
              commit("SET_GROUP_LIST", {
                favoriteKey: favoriteKey,
                groupList: groups,
              });
            });
          commit("ADD_GROUP_UNSUBSCRIBES", unsubscribe);
        })
      );
    }

    Promise.all(promise);
  },

  async unsubscribeGroups({ commit }) {
    commit("UNSUBSCRIBE_GROUP");
  },

  async fetchGroups({ commit, dispatch }) {
    // firestoreからお気に入りとグループを読み込む
    // 一つでもグループに所属している船舶はmonitoringVesselに登録し、データ監視対象にする
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    const favoriteKeys = ["0", "1", "2"];
    const promise = [];
    let vesselUniqueIds = [];
    for (const favoriteKey of favoriteKeys) {
      promise.push(
        new Promise((resolve, _) => {
          db.collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey)
            .collection("groups")
            .get()
            .then(querySnapshot => {
              const groups = [];
              querySnapshot.forEach(doc => {
                if (doc.id !== "existFlag") {
                  groups.push({ documentId: doc.id, ...doc.data() });
                  doc.data().vessels.forEach(vesselId => {
                    vesselUniqueIds.push(vesselId);
                  });
                }
              });
              commit("SET_GROUP_LIST", {
                favoriteKey: favoriteKey,
                groupList: groups,
              });
              resolve();
            });
        })
      );
    }

    Promise.all(promise).then(() => {
      vesselUniqueIds = Array.from(new Set(vesselUniqueIds));
      vesselUniqueIds.forEach(vesselId => {
        dispatch("addMonitoringVessel", vesselId);
      });
    });
  },

  async getFavoriteLabels({ commit }) {
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    const favoriteKeys = ["0", "1", "2"];
    const promise = [];
    for (const favoriteKey of favoriteKeys) {
      promise.push(
        new Promise((resolve, _) => {
          db.collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey)
            .get()
            .then(doc => {
              if (doc.exists) {
                commit("SET_FAVORITE_LABEL", {
                  favoriteKey: favoriteKey,
                  label: doc.data().label,
                });
                resolve(doc.data().label);
              }
            });
        })
      );
    }

    let labelList;
    await Promise.all(promise).then(result => {
      labelList = result;
    });
    return labelList;
  },

  async changeFavoriteLabel({ commit }, payload) {
    const index = payload.index;
    const newLabel = payload.label;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(index.toString())
      .set({
        label: newLabel,
      });

    commit("SET_FAVORITE_LABEL", { favoriteKey: index, label: newLabel });
  },

  async updateGroupSortOrder(_, payload) {
    const sortList = payload.sortList;
    const favoriteKey = payload.favoriteKey;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    const promise = [];
    sortList.forEach(sortObject => {
      promise.push(
        new Promise((resolve, _) => {
          db.collection("accounts")
            .doc(firebaseAccountId)
            .collection("setting")
            .doc("selected_vessel_group")
            .collection("favorites")
            .doc(favoriteKey.toString())
            .collection("groups")
            .doc(sortObject.documentId)
            .set(
              {
                sort: sortObject.sort,
              },
              { merge: true }
            )
            .then(() => {
              resolve();
            });
        })
      );
    });

    Promise.all(promise);
  },

  async executeGroupAddSave({ state }, payload) {
    const favoriteKey = payload.favoriteKey;
    const groupInfo = payload.groupInfo;
    const sortList = state.favoriteList[favoriteKey].groupList.map(
      element => element.sort
    );
    const maxSort = sortList.length != 0 ? Math.max(...sortList) : 0;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .add({
        color: groupInfo.color,
        label: groupInfo.label,
        sort: maxSort + 1,
        vessels: [],
      });
  },

  async executeGroupEditSave(_, payload) {
    const favoriteKey = payload.favoriteKey;
    const groupInfo = payload.groupInfo;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();
    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .doc(groupInfo.documentId)
      .set(
        {
          color: groupInfo.color,
          label: groupInfo.label,
        },
        { merge: true }
      );
  },

  async executeGroupEditDelete({ dispatch, state }, payload) {
    const favoriteKey = payload.favoriteKey;
    const groupInfo = payload.groupInfo;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();

    let vesselIdList = [];

    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .doc(groupInfo.documentId)
      .get()
      .then(doc => {
        if (doc.exists) {
          vesselIdList = doc.data().vessels;
        }
      });

    vesselIdList.forEach(async targetVesselId => {
      let vesselIdsAccrossFavorites = [];
      state.favoriteList.forEach(favorite => {
        favorite.groupList.forEach(group => {
          group.vessels.forEach(vesselId => {
            vesselIdsAccrossFavorites.push(vesselId);
          });
        });
      });
      const shouldRemoveMonitoring =
        vesselIdsAccrossFavorites.filter(element => targetVesselId == element)
          .length == 1;
      if (shouldRemoveMonitoring)
        await dispatch("removeMonitoringVessel", targetVesselId);
    });

    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .doc(groupInfo.documentId)
      .delete();
  },

  async deleteVesselFromGroup({ dispatch, state }, payload) {
    const favoriteKey = payload.favoriteKey;
    const vesselId = payload.vesselId;
    const groupDocumentId = payload.groupDocumentId;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();

    let vesselIdsAccrossFavorites = [];
    state.favoriteList.forEach(favorite => {
      favorite.groupList.forEach(group => {
        group.vessels.forEach(vesselId => {
          vesselIdsAccrossFavorites.push(vesselId);
        });
      });
    });
    const shouldRemoveMonitoring =
      vesselIdsAccrossFavorites.filter(element => vesselId == element).length ==
      1;
    if (shouldRemoveMonitoring)
      await dispatch("removeMonitoringVessel", vesselId);

    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .doc(groupDocumentId)
      .update({
        vessels: firebase.firestore.FieldValue.arrayRemove(vesselId),
      });
  },

  async addVesselToGroup({ dispatch, state }, payload) {
    const favoriteKey = payload.favoriteKey;
    const vesselId = payload.vesselId;
    const groupDocumentId = payload.groupDocumentId;
    const firebaseAccountId = await FireStoreService.getFirebaseAccountId();

    let vesselIdsAccrossFavorites = [];
    state.favoriteList.forEach(favorite => {
      favorite.groupList.forEach(group => {
        group.vessels.forEach(vesselId => {
          vesselIdsAccrossFavorites.push(vesselId);
        });
      });
    });
    const shouldStartMonitor =
      vesselIdsAccrossFavorites.filter(element => vesselId == element).length ==
      0;
    if (shouldStartMonitor) await dispatch("addMonitoringVessel", vesselId);

    await db
      .collection("accounts")
      .doc(firebaseAccountId)
      .collection("setting")
      .doc("selected_vessel_group")
      .collection("favorites")
      .doc(favoriteKey.toString())
      .collection("groups")
      .doc(groupDocumentId)
      .update({
        vessels: firebase.firestore.FieldValue.arrayUnion(vesselId),
      });
  },

  /*=========================================================================================
   海上設置物 Actions
  ==========================================================================================*/
  async getFloatingUnits({ commit }) {
    let floatingUnits = [];
    db.collection("floating_unit")
      .get()
      .then(querySnapshot => {
        querySnapshot.forEach(doc => {
          floatingUnits.push(doc.data());
        });
        commit("SET_FLOATING_UNITS", floatingUnits);
      })
      .catch(error => {
        Vue.$log.error(error);
      });
  },

  /*=========================================================================================
   リセット
  ==========================================================================================*/
  async resetLiveMapState({ commit }) {
    let defaultState = {
      showVesselDetail: false,
      showPlotDetail: false,
      unsubscribes: [],
      vesselDetail: null,
      plotDetail: null,
      monitoringState: false,
      vesselPictures: null,
      vesselCameras: [],
      vesselRadars: [],
      vesselDetailInfo: null,
      vesselDetailPosition: null,
      countryCodes: [],
      ownVessels: [],
      ownVesselPosition: Map(),
      ownVesselPointFeatureCollection: [],
      ownVesselPolygonFeatureCollection: [],
      ownVesselVectorFeatureCollection: [],
      ownVesselDisplayDurationTime: 0,
      monitoringVessels: [],
      selectedVesselPosition: Map(),
      selectedVesselPointFeatureCollection: [],
      selectedVesselPolygonFeatureCollection: [],
      selectedVesselVectorFeatureCollection: [],
      isNewSelectedVessel: false,
      metoceanInitialDatetime: null,
      oldWeatherSourceLayerId: null,
      currentWeatherSourceLayerId: null,
      weatherCurrentDatetime: null,
      selectedMetoceanElement: null,
      showWeatherMenu: false,
      showContour: false,
      showMetoceanValue: true,
      showMetoceanGrid: true,
      isTimeSyncMode: false,
      updateMetoceanSourceLayer: null,
      updateContourSourceLayer: null,
      activeLayersSidebar: false,
      activeSearchSidebar: false,
      activeOwnVesselSidebar: false,
      activeSelectVesselSidebar: false,
      activeFilterSidebar: false,
      activePlotSidebar: false,
      activeWeatherSidebar: false,
      activeSettingsSidebar: false,
      offingWavePoints: [],
      dialogOffingWavePointInputForm: null,
      offingWavePointInputValues: null,
      typhoonInfoList: null,
      typhoonLayerVisibility: true,
      jumpTo: null,
      jumpToMap: null,
      mapStyle: null,
      searchResult: {
        vessel: {
          key: "name",
          data: [],
        },
      },
      wakes: Map(),
      oldestVesselId: null,
      wakeLineFeatureCollection: [],
      filterVessels: [],
      filterPlots: [],
      selectedFixedPointCamera: null,
      floatingUnits: [],
    };
    commit("RESET_STATE", defaultState);
  },
};
